/**
 * Validació de completitud per Mossos d'Esquadra (RD 933/2021)
 * Determina si un viatger té totes les dades obligatòries per ser reportat
 *
 * Retorna els noms de camp que falten; el text el posa i18n qui el mostra
 * (FormulariPublicPage), perquè el formulari públic no porti text en català fix.
 */
import { hasMossosIssue, mossosTravellerIssues } from '@guilleumes/types'

export interface ViatgerData {
  nom?: string
  firstName?: string
  cognoms?: string
  cognom1?: string
  lastName?: string
  lastName1?: string
  data_naixement?: string | Date
  dateOfBirth?: string | Date
  sexe?: string
  sex?: string
  nacionalitat?: string
  nationality?: string
  tipus_document?: string
  documentType?: string
  num_document?: string
  documentNumber?: string
  num_suport?: string
  campSuport?: string
  supportNumber?: string
  data_caducitat?: string | Date
  documentExpiry?: string | Date
}

export interface MossosValidationResult {
  isComplete: boolean
  missingFields: string[]
  completionPercentage: number
}

/**
 * Camps obligatoris per Mossos segons RD 933/2021
 */
const REQUIRED_FIELDS = [
  'nom',           // Nom
  'cognoms',       // Cognoms (mínim 1)
  'data_naixement', // Data de naixement
  'sexe',          // Sexe
  'nacionalitat',  // Nacionalitat
  'tipus_document', // Tipus de document
  'num_document',  // Número de document
]

/**
 * Verifica si un viatger té totes les dades necessàries per Mossos.
 *
 * La regla és la compartida (`mossosTravellerIssues`, a `@guilleumes/types`); aquí només es tria
 * quins codis compten per a la targeta «ja registrats» i amb quin nom de camp es diuen. Sense
 * data de naixement el document es demana igualment (no se sap si en necessita).
 */
export function isMossosComplete(viatger: ViatgerData, today: Date = new Date()): MossosValidationResult {
  const missingFields: string[] = []
  
  // Normalitzar camps (suportar múltiples formats: català, anglès, camelCase)
  const nom = viatger.nom || viatger.firstName || ''
  const cognoms = viatger.cognoms || viatger.lastName || viatger.cognom1 || viatger.lastName1 || ''
  const dataNaixement = viatger.data_naixement || viatger.dateOfBirth
  const sexe = viatger.sexe || viatger.sex || ''
  const nacionalitat = viatger.nacionalitat || viatger.nationality || ''
  const tipusDocument = viatger.tipus_document || viatger.documentType || ''
  const numDocument = viatger.num_document || viatger.documentNumber || ''

  // Un text només d'espais compta com a buit (nom, cognoms i número de document)
  const issues = mossosTravellerIssues(
    {
      firstName: nom?.trim(),
      lastName: cognoms?.trim(),
      dateOfBirth: dataNaixement,
      sex: sexe,
      nationality: nacionalitat,
      documentType: tipusDocument,
      documentNumber: numDocument?.trim(),
    },
    today,
  )

  // Document obligatori només a partir de 14 anys (edat exacta, com el formulari i el backend)
  if (hasMossosIssue(issues, 'FIRST_NAME')) missingFields.push('nom')
  if (hasMossosIssue(issues, 'LAST_NAME')) missingFields.push('cognoms')
  if (hasMossosIssue(issues, 'BIRTH')) missingFields.push('data_naixement')
  if (hasMossosIssue(issues, 'SEX')) missingFields.push('sexe')
  if (hasMossosIssue(issues, 'NATIONALITY')) missingFields.push('nacionalitat')
  if (hasMossosIssue(issues, 'DOC_TYPE')) missingFields.push('tipus_document')
  if (hasMossosIssue(issues, 'DOCUMENT', 'DOCUMENT_UNKNOWN_AGE')) missingFields.push('num_document')

  // NOTA: campSuport (IDESP) i la caducitat del passaport no són obligatoris (RD 933/2021,
  // annex I; el V24 no envia la caducitat). Abans la caducitat es demanava i, com que
  // forms.service no la torna, tot passaport ja registrat sortia com a «Falten dades».

  // Calcular percentatge de completitud
  const totalFields = REQUIRED_FIELDS.length
  const completedFields = totalFields - missingFields.length
  const completionPercentage = Math.round((completedFields / totalFields) * 100)

  return {
    isComplete: missingFields.length === 0,
    missingFields,
    completionPercentage,
  }
}

